/**
 * Cr8Her Creator Content (Content tab -> Instagram tab) -- Tab build, Oct 25 2026.
 * Mirrors Urban Unity's Content tab one-to-one (same tab structure: YouTube / Instagram only,
 * no separate Content Plan, no separate analytics UI -- per Joey's 5:41 PM feedback) but for
 * Cr8Her, which has no Callix: 5 buyer avatars were derived by Tab from all 104 Fireflies sales
 * calls on record, and every video/call is scored against them (5 fields, 0-3 each, verbatim
 * quote required; below threshold or a near-tie = Unclassified). Reel<->call matches are judged
 * on MEANING by an LLM, not exact shared words. Per Joey's 6:49 PM steer ("I want the videos to
 * be the conversion... have the cash collected of the videos be personalized to that video in the
 * time frame"), there is no avatar-level fallback pool: a video's calls/closes/cash are ONLY the
 * calls that (a) occurred inside the page's own date filter, (b) came from that video's own
 * creator, and (c) were judged a real semantic match to that specific video. No match in range =
 * $0, no matched calls. Each call links to its Fireflies transcript and, where a prospect-name
 * match found one, its EOC / cash record. Call cards show no inline transcript (Copy + .srt only)
 * and meet WCAG AA contrast from the start.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { useDateRange } from "../context/DateRangeContext";

interface EocRecord {
  prospectName?: string; closerName?: string; cashCollected?: number; revenueGenerated?: number;
  callOutcome?: string; paymentPlan?: string; sourceCreator?: string; sourcePlatform?: string;
}
interface MatchedCall {
  callKey: string; title: string; occurredAt: string;
  buyerLanguage: { buyerName: string | null; verbatimPhrases: string[]; pains: string[]; desires: string[]; objectionsTriggers: string[]; noSignal: boolean };
  avatarKey: string | null;
  transcriptUrl: string | null;
  eocRecord: EocRecord | null;
  matchReason: string; videoEvidence: string; callEvidence: string;
  confidence: "high" | "medium" | "low"; inferred: true;
  lowConfidence: boolean; creatorUnknown: boolean;
}
interface FieldScore { score: number; quote: string }
interface ScoreV4 { topTotal: number; runnerUpKey: string | null; runnerUpTotal: number; threshold: number; scoreTotals: Record<string, number>; fieldScores: Record<string, Record<string, FieldScore>> }
interface VideoData {
  accountKey: string; platform: "tiktok" | "instagram"; creator: string; handle: string;
  externalId: string; url: string; postedAt: string; caption: string; durationSec: number | null;
  transcript: string; noAudioTranscript: boolean; hasThumb: boolean;
  likeCount: number | null; commentCount: number | null; shareCount: number | null; saveCount: number | null; viewCount: number | null;
  avatarKey: string | null; unclassified: boolean; unclassifiedReason: string; scoreV4: ScoreV4 | null;
  cashCollected: number; closedCount: number;
  matchedCalls: MatchedCall[]; callsCount: number;
}
interface ContentPayload {
  range: { startDate: string; endDate: string };
  totals: { videos: number };
  videos: VideoData[];
  unclassifiedVideos: VideoData[];
  inferredNote: string;
}
interface AvatarProfile {
  avatarKey: string; title: string; subtitle: string; short: string;
  demographics: string; psychographicProfile: string;
  painPoints: string[]; desires: string[]; objectionsTriggers: string[]; languageBank: string[];
  exampleCalls: string[]; derivedFrom: string;
}
interface AvatarConversionRow { avatarKey: string; title: string; callsHeld: number; closes: number; cashCollected: number; rate: number | null }
interface AvatarConversionPayload { range: { startDate: string; endDate: string }; rows: AvatarConversionRow[]; topAvatarKey: string | null; definition: string }

const ACCOUNTS = [
  { key: "kay_tiktok", label: "Kay (TikTok)", platform: "tiktok" },
  { key: "sav_tiktok", label: "Sav (TikTok)", platform: "tiktok" },
  { key: "storie_tiktok", label: "Storie (TikTok)", platform: "tiktok" },
  { key: "sav_instagram", label: "Sav (Instagram)", platform: "instagram" },
] as const;

const DASH = String.fromCharCode(0x2014);
const MID = String.fromCharCode(0xb7);
const HEART = String.fromCharCode(0x2764) + String.fromCharCode(0xfe0f);
const SPEECH = String.fromCharCode(0x1f4ac);
const EYE = String.fromCharCode(0x1f441) + String.fromCharCode(0xfe0f);
const LQ = String.fromCharCode(0x201c);
const RQ = String.fromCharCode(0x201d);

function fmtNum(n: number | null): string { return n == null ? DASH : n.toLocaleString(); }
function fmtMoney(n: number | undefined | null): string { return n == null ? "$0" : "$" + n.toLocaleString(); }
function fmtPct(rate: number | null): string { return rate == null ? DASH : (rate * 100).toFixed(1) + "%"; }

export function CreatorAvatarContent() {
  const { formatForApi, formatForDisplay } = useDateRange();
  const [data, setData] = useState<ContentPayload | null>(null);
  const [avatars, setAvatars] = useState<AvatarProfile[]>([]);
  const [conversion, setConversion] = useState<AvatarConversionPayload | null>(null);
  const [error, setError] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [openAvatar, setOpenAvatar] = useState<string | null>(null);
  const [creatorFilter, setCreatorFilter] = useState<string>("all");
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const apiDates = formatForApi();

  useEffect(() => {
    let off = false;
    setData(null); setError("");
    const params = new URLSearchParams({ startDate: apiDates.startDate, endDate: apiDates.endDate });
    fetch(`/api/content/creator-content?${params}`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (off) return;
      if (j.success) setData(j.data); else setError(j.error || `HTTP ${r.status}`);
    }).catch((e) => !off && setError(String(e)));
    fetch(`/api/content/creator-avatars`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (!off && j.success) setAvatars(j.data);
    }).catch(() => {});
    fetch(`/api/content/avatar-conversion?${params}`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (!off && j.success) setConversion(j.data);
    }).catch(() => {});
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiDates.startDate, apiDates.endDate]);

  const avatarByKey = useMemo(() => {
    const m: Record<string, AvatarProfile> = {};
    avatars.forEach((a) => { m[a.avatarKey] = a; });
    return m;
  }, [avatars]);

  const conversionByKey = useMemo(() => {
    const m: Record<string, AvatarConversionRow> = {};
    (conversion?.rows || []).forEach((r) => { m[r.avatarKey] = r; });
    return m;
  }, [conversion]);
  // No Unclassified card: every call is force-classified to one of the 5 avatars (Joey, Oct 25
  // 7:23pm, "I don't want to see blind data"), so conversionByKey never holds an "unclassified" key.

  const creators = useMemo(() => [...new Set(ACCOUNTS.map((a) => a.label.split(" (")[0]))], []);

  const filteredVideos = useMemo(() => {
    if (!data) return [];
    return data.videos.filter((v) => {
      if (accountFilter !== "all" && v.accountKey !== accountFilter) return false;
      if (creatorFilter !== "all" && v.creator !== creatorFilter) return false;
      return true;
    });
  }, [data, accountFilter, creatorFilter]);

  const openVideo = useMemo(() => data?.videos.find((v) => `${v.accountKey}:${v.externalId}` === openKey) || null, [data, openKey]);

  const perAccountCounts = useMemo(() => {
    const m: Record<string, number> = {};
    (data?.videos || []).forEach((v) => { m[v.accountKey] = (m[v.accountKey] || 0) + 1; });
    return m;
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Avatar strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {avatars.map((a) => {
          const c = conversionByKey[a.avatarKey];
          const isTop = !!conversion?.topAvatarKey && conversion.topAvatarKey === a.avatarKey;
          return (
            <button key={a.avatarKey} onClick={() => setOpenAvatar(a.avatarKey)}
              className={`text-left rounded-xl border bg-card hover:bg-secondary/40 transition-colors p-3 ${isTop ? "border-emerald-500 ring-1 ring-emerald-500/40" : "border-border"}`}>
              <div className="flex items-start justify-between gap-1">
                <p className="text-sm font-semibold text-foreground leading-tight">{a.title}</p>
                {isTop && <span className="text-[9px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-emerald-600 text-white whitespace-nowrap">Top converting</span>}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{a.subtitle}</p>
              {c && c.callsHeld > 0 ? (
                <>
                  <p className="text-[11px] font-semibold text-foreground mt-1.5">Converted {c.closes}/{c.callsHeld} ({fmtPct(c.rate)})</p>
                  <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 mt-0.5">Cash collected {fmtMoney(c.cashCollected)}</p>
                </>
              ) : (
                <p className="text-[11px] text-muted-foreground mt-1.5">No calls held in this window</p>
              )}
            </button>
          );
        })}
      </div>
      {conversion && (
        <p className="text-[10px] text-muted-foreground px-1 -mt-3">{conversion.definition}</p>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs font-medium text-muted-foreground">
          Showing <span className="text-foreground font-semibold">{formatForDisplay()}</span> {DASH} videos are filtered by post date, and so are their matched sales calls (a call only counts here if it happened in this range).
        </p>
        <span className="text-[11px] text-muted-foreground max-w-md text-right">
          No Callix on this account {DASH} avatars &amp; matches are Tab's own read of 104 Fireflies sales calls. Every tag is inferred.
        </span>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center rounded-lg border border-border bg-secondary/30 p-1 gap-1">
          <button onClick={() => setCreatorFilter("all")} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${creatorFilter === "all" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>All creators</button>
          {creators.map((c) => (
            <button key={c} onClick={() => setCreatorFilter(c)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${creatorFilter === c ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>{c}</button>
          ))}
        </div>
        <div className="inline-flex items-center rounded-lg border border-border bg-secondary/30 p-1 gap-1">
          <button onClick={() => setAccountFilter("all")} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${accountFilter === "all" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>All accounts</button>
          {ACCOUNTS.map((a) => (
            <button key={a.key} onClick={() => setAccountFilter(a.key)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${accountFilter === a.key ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              {a.label} <span className="opacity-70">({perAccountCounts[a.key] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-red-500 px-1">Couldn't load content: {error}</div>}
      {!data && !error && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {[0,1,2,3,4,5,6,7,8,9].map((i) => <div key={i} className="h-64 rounded-xl bg-secondary/30 animate-pulse" />)}
        </div>
      )}

      {data && filteredVideos.length === 0 && (
        <div className="flex items-center justify-center p-10 rounded-xl border border-dashed border-border bg-card/50">
          <p className="text-sm font-medium text-foreground">No videos in the selected range for this filter</p>
        </div>
      )}

      {data && filteredVideos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredVideos.map((v) => {
            const key = `${v.accountKey}:${v.externalId}`;
            const av = v.avatarKey ? avatarByKey[v.avatarKey] : null;
            return (
              <motion.div key={key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => setOpenKey(key)}>
                  <div style={{ aspectRatio: "9/16" }} className="bg-secondary/40 flex items-center justify-center relative overflow-hidden">
                    <span className="text-[10px] uppercase tracking-wide absolute top-2 left-2 bg-black/60 text-white rounded px-1.5 py-0.5 z-10">{v.platform}</span>
                    {v.noAudioTranscript && <span className="text-[9px] absolute top-2 right-2 bg-amber-600 text-white rounded px-1.5 py-0.5 z-10">no audio</span>}
                    {v.hasThumb ? (
                      <img src={`/api/content/creator-content/thumbnail?accountKey=${v.accountKey}&externalId=${v.externalId}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-muted-foreground px-3 text-center">{v.caption?.slice(0, 80) || "(no caption)"}</span>
                    )}
                  </div>
                  <CardContent className="p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-foreground">{v.creator} {MID} {new Date(v.postedAt).toLocaleDateString()}</p>
                    <p className="text-[11px] text-muted-foreground">{EYE} {fmtNum(v.viewCount)} {MID} {HEART} {fmtNum(v.likeCount)} {MID} {SPEECH} {fmtNum(v.commentCount)}</p>
                    {v.avatarKey ? (
                      <span className="inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]">{av?.title || v.avatarKey}</span>
                    ) : (
                      <span className="inline-block text-[10px] font-medium rounded-full px-2 py-0.5 bg-secondary text-muted-foreground">Unclassified</span>
                    )}
                    {v.callsCount > 0 ? (
                      <p className="text-[12px] font-bold text-emerald-700 dark:text-emerald-400">{v.callsCount} call{v.callsCount === 1 ? "" : "s"} {MID} {v.closedCount} closed {MID} {fmtMoney(v.cashCollected)} collected</p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">$0 {MID} no matched calls in range</p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Video detail modal */}
      <Dialog open={!!openVideo} onOpenChange={(o) => !o && setOpenKey(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {openVideo && <VideoDetail video={openVideo} avatar={openVideo.avatarKey ? avatarByKey[openVideo.avatarKey] : null} />}
        </DialogContent>
      </Dialog>

      {/* Avatar detail modal */}
      <Dialog open={!!openAvatar} onOpenChange={(o) => !o && setOpenAvatar(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {openAvatar && avatarByKey[openAvatar] && <AvatarDetail avatar={avatarByKey[openAvatar]} />}
        </DialogContent>
      </Dialog>

      {data && (
        <p className="text-[11px] text-muted-foreground px-1 pt-2 border-t border-border">{data.inferredNote}</p>
      )}
    </div>
  );
}

function AvatarDetail({ avatar }: { avatar: AvatarProfile }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-foreground">{avatar.title}</h3>
        <p className="text-sm text-muted-foreground">{avatar.subtitle}</p>
      </div>
      <p className="text-sm text-foreground">{avatar.short}</p>
      <div>
        <p className="text-xs font-semibold text-foreground mb-1">Demographics</p>
        <p className="text-sm text-muted-foreground">{avatar.demographics}</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-foreground mb-1">Psychographic profile</p>
        <p className="text-sm text-muted-foreground">{avatar.psychographicProfile}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold text-foreground mb-1">Pain points</p>
          <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-0.5">{avatar.painPoints.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-foreground mb-1">Desires</p>
          <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-0.5">{avatar.desires.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-foreground mb-1">Objections &amp; triggers</p>
        <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-0.5">{avatar.objectionsTriggers.map((p, i) => <li key={i}>{p}</li>)}</ul>
      </div>
      <div>
        <p className="text-xs font-semibold text-foreground mb-1">Language bank</p>
        <div className="flex flex-wrap gap-1.5">{avatar.languageBank.map((t, i) => <span key={i} className="text-[11px] rounded-full px-2 py-0.5 bg-secondary text-foreground">{t}</span>)}</div>
      </div>
      <p className="text-[11px] text-muted-foreground italic">{avatar.derivedFrom}</p>
    </div>
  );
}

function VideoDetail({ video, avatar }: { video: VideoData; avatar: AvatarProfile | null }) {
  const shown = video.matchedCalls;
  const copyTranscript = () => { navigator.clipboard?.writeText(video.transcript || video.caption || ""); };
  const downloadSrt = () => {
    const text = video.transcript || video.caption || "";
    const srt = "1\n00:00:00,000 --> 00:00:30,000\n" + text + "\n";
    const blob = new Blob([srt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${video.accountKey}_${video.externalId}.srt`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-foreground">{video.creator} {DASH} {video.platform} {MID} {new Date(video.postedAt).toLocaleDateString()}</h3>
          <a href={video.url} target="_blank" rel="noreferrer" className="text-xs text-[var(--brand-primary)] underline">{video.url}</a>
        </div>
        {video.avatarKey && <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-[var(--brand-primary)]/15 text-[var(--brand-primary)] whitespace-nowrap">{avatar?.title || video.avatarKey}</span>}
      </div>
      <p className="text-sm text-foreground whitespace-pre-wrap">{video.caption}</p>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span>{EYE} {fmtNum(video.viewCount)} views</span>
        <span>{HEART} {fmtNum(video.likeCount)} likes</span>
        <span>{SPEECH} {fmtNum(video.commentCount)} comments</span>
        {video.shareCount != null && <span>{fmtNum(video.shareCount)} shares</span>}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={copyTranscript} className="text-xs font-medium rounded-md border border-border px-2.5 py-1 hover:bg-secondary/50">Copy transcript</button>
        <button onClick={downloadSrt} className="text-xs font-medium rounded-md border border-border px-2.5 py-1 hover:bg-secondary/50">Download .srt</button>
        {video.noAudioTranscript && <span className="text-[11px] text-muted-foreground">No audio transcript available (Instagram, no login)</span>}
      </div>
      {video.unclassified && (
        <p className="text-xs text-muted-foreground italic">Unclassified: {video.unclassifiedReason || "no avatar scored high enough / near-tie"}</p>
      )}

      <div className="pt-2 border-t border-border">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm font-semibold text-foreground">Matched sales calls in range ({video.matchedCalls.length}) {MID} {video.closedCount} closed {MID} {fmtMoney(video.cashCollected)} collected</p>
        </div>
        {shown.length === 0 && <p className="text-xs text-muted-foreground">No matched calls in this date range.</p>}
        <div className="space-y-3">
          {shown.map((m) => {
            const eoc = m.eocRecord;
            const closedCash = eoc && typeof eoc.cashCollected === "number" && eoc.cashCollected > 0;
            return (
            <div key={m.callKey} className="rounded-lg border border-border p-3 bg-card">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-semibold text-foreground">{m.title}</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{new Date(m.occurredAt).toLocaleDateString()}</span>
                  {m.transcriptUrl && <a href={m.transcriptUrl} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-[var(--brand-primary)] underline">Open in Fireflies</a>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-emerald-600 text-white">
                  Matches this video's theme
                </span>
                {m.lowConfidence && (
                  <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300" title="This call had little or no usable transcript/notes, or the best available match for this creator was a weak one -- the pick is forced-choice, not a strong semantic match">
                    Low confidence match
                  </span>
                )}
                {m.creatorUnknown && (
                  <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-300" title="This lead's source creator (Kay/Sav/Storie) couldn't be determined, so it was matched across every creator's videos instead of being gated to one">
                    Creator unknown
                  </span>
                )}
                {eoc && (
                  <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${closedCash ? "bg-emerald-50 text-emerald-900 border-emerald-300" : "bg-secondary text-muted-foreground border-border"}`}>
                    EOC: {eoc.callOutcome || "logged"}{closedCash ? ` \u2014 ${fmtMoney(eoc.cashCollected)} collected` : ""}{eoc.closerName ? ` (${eoc.closerName})` : ""}
                  </span>
                )}
              </div>
              <p className="text-xs text-foreground mt-2">{m.matchReason}</p>
              {m.videoEvidence && <p className="text-xs text-muted-foreground mt-1"><span className="font-semibold text-foreground">Said in the video:</span> {LQ}{m.videoEvidence}{RQ}</p>}
              {m.callEvidence && <p className="text-xs text-muted-foreground mt-1"><span className="font-semibold text-foreground">Said on the call:</span> {LQ}{m.callEvidence}{RQ}</p>}
              {m.buyerLanguage && !m.buyerLanguage.noSignal && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {m.buyerLanguage.pains.length > 0 && (
                    <div><p className="text-[10px] font-semibold text-foreground">Pains</p><ul className="text-[11px] text-muted-foreground list-disc pl-3">{m.buyerLanguage.pains.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
                  )}
                  {m.buyerLanguage.desires.length > 0 && (
                    <div><p className="text-[10px] font-semibold text-foreground">Desires</p><ul className="text-[11px] text-muted-foreground list-disc pl-3">{m.buyerLanguage.desires.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

